import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, first } from 'rxjs/operators';
import { ComponentCanDeactivate } from '@app/core/guards/unsaved-changes.guard';
import { ModalComponent } from '@app/shared/components/modal/modal.component';

export interface DialogOptions {
	title: string;
	message: string;
	subtitle: string;
	cancelText: string;
	cancelColor: ThemePalette;
	showCancel: boolean;
	okText: string;
	okColor: ThemePalette;
	okHidden: boolean;
	disableClose: boolean;
	form?: UntypedFormGroup;
	fields?: FormlyFieldConfig[];
	options?: FormlyFormOptions;
	model?: any;
	minWidth?: string;
	customButtons?: { text: string; action: () => any; useMatMenu?: boolean }[];
}

export interface CanDeactivateConfirmOptions {
	onCancel?: () => void;
	onCanDeactivate?: () => void;
}

@Injectable()
export class DialogService {
	public modalOpened$ = new BehaviorSubject<boolean | any>(false);

	private unsavedDialogOpened$: Subject<boolean> = null;
	private readonly unsavedDialogTitle = 'Exit Without Saving?';

	constructor(private readonly dialog: MatDialog) {
		this.dialog.afterOpened.subscribe(() => {
			this.modalOpened$.next(true);
		});

		this.dialog.afterAllClosed.subscribe(() => {
			this.modalOpened$.next(false);
		});
	}

	/**
	 * Checks if a ComponentCanDeactivate can deactivate
	 */
	public canComponentDeactivate(component: ComponentCanDeactivate): boolean {
		return component.canDeactivate();
	}

	/**
	 * Displays a confirmation modal if cannot deactivate
	 */
	public canDeactivate(component: ComponentCanDeactivate, options?: CanDeactivateConfirmOptions): Observable<boolean> {
		// if can deactivate => true
		// if can't deactivate and canceled => false
		// if can't deactivate and confirmed => true
		const confirmed$ = new Subject<boolean>();
		const canDeactivate = this.canComponentDeactivate(component);
		if (canDeactivate) {
			setTimeout(() => {
				confirmed$.next(canDeactivate);
				confirmed$.complete();
				options?.onCanDeactivate?.();
			}, 0);
		} else {
			this.confirmUnsavedChanges()
				.pipe(first())
				.subscribe((closed) => {
					confirmed$.next(closed);
					confirmed$.complete();
					if (closed) {
						options?.onCanDeactivate?.();
					} else {
						options?.onCancel?.();
					}
				});
		}

		return confirmed$;
	}

	public confirmUnsavedChanges(data?: DialogOptions): Subject<boolean> {
		// prevent duplicate unsaved changes modals when pressing back button multiple times
		if (this.unsavedDialogOpened$) {
			return this.unsavedDialogOpened$;
		}

		this.unsavedDialogOpened$ = this.open({
			title: this.unsavedDialogTitle,
			message: 'There are unsaved changes on this panel. Exit without saving?',
			okText: 'Continue',
			...data,
		} as DialogOptions);

		this.unsavedDialogOpened$
			.pipe(
				finalize(() => {
					this.unsavedDialogOpened$ = null;
				})
			)
			.subscribe();

		return this.unsavedDialogOpened$;
	}

	public open(data: Partial<DialogOptions>): Subject<boolean> {
		const opened$ = new Subject<boolean>();

		const dialogRef = this.dialog.open(ModalComponent, {
			minWidth: data?.minWidth ?? undefined,
			maxWidth: data?.minWidth ?? '800px',
			disableClose: data.disableClose,
			data: {
				...data,
			},
		});

		dialogRef.afterClosed().subscribe((dialogResult) => {
			opened$.next(dialogResult);
			opened$.complete();
		});

		return opened$;
	}

	public closeAll(): void {
		this.dialog.closeAll();
	}
}
